<template>
  <!-- Contenedor de detalles de selección - Estilo Material Design 3 Compacto -->
  <div class="bg-gray-50 rounded-xl border border-gray-200 shadow-sm overflow-hidden">
    <!-- Header del contenedor -->
    <div class="px-3 py-2 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
      <h3 class="text-sm font-semibold text-gray-700 tracking-wide">
        {{ $t('details_of_selected_elements') }}
      </h3>
    </div>

    <!-- Contenido del contenedor -->
    <div class="px-3 py-2">
      <!-- Resumen superior compacto -->
      <div class="grid-x grid-cols-2-x gap-2-x mb-1">
        <!-- Sin estado -->
        <div class="flex items-center justify-between py-1.5 px-2 bg-white rounded-md border border-gray-200 hover:border-gray-300 transition-colors">
          <div class="flex items-center gap-1.5">
            <div class="w-2 h-2 rounded bg-gray-400 flex-shrink-0 border border-gray-500 shadow-sm"></div>
            <span class="text-xs font-medium text-gray-700 truncate">{{ $t(typeStatus === 'incident' ? 'no_incident_status' : 'no_status_2') }}</span>
          </div>
          <span class="text-xs font-semibold text-gray-900 bg-gray-50 px-1.5 py-0.5 rounded flex-shrink-0 ml-1">
            {{ groupedByStatus[null] ? groupedByStatus[null] : 0 }}
            <span v-if="company.alias === 'x-elio' && layer.code === 'modules'">({{ groupByStatusDesign[null] ? groupByStatusDesign[null] : 0 }})</span>
          </span>
        </div>
      </div>

      <!-- Estados en grid compacto con scroll -->
      <div class="max-h-48 overflow-y-auto pr-1 custom-scrollbar">
        <div class="grid grid-cols-2 gap-2">
          <div
            v-for="s in statuses"
            :key="s.id"
            class="flex items-center justify-between py-1.5 px-2 bg-white rounded-md border border-gray-200 hover:border-gray-300 transition-colors"
          >
            <div class="flex items-center gap-1.5 min-w-0 flex-1">
              <div
                class="w-2 h-2 rounded flex-shrink-0 border border-gray-400 shadow-sm"
                :style="{ backgroundColor: s.color || '#6366f1' }"
              ></div>
              <span class="text-xs font-medium text-gray-700 truncate">{{ s.name }}</span>
            </div>
            <span
              class="text-xs font-semibold text-gray-900 px-1.5 py-0.5 rounded flex-shrink-0 ml-1"
              :style="{ backgroundColor: (s.color || '#6366f1') + '15' }"
            >
              {{ groupedByStatus[s.id] ? groupedByStatus[s.id] : 0 }}
            </span>
            <span v-if="company.alias === 'x-elio' && layer.code === 'modules'"
              class="text-xs font-semibold text-gray-900 bg-gray-50 px-1.5 py-0.5 rounded flex-shrink-0 ml-1">
              ({{ groupByStatusDesign[s.id] ? groupByStatusDesign[s.id] : 0 }})
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer del contenedor -->
    <div class="px-3 py-2 bg-gradient-to-r from-gray-50 to-gray-100 border-t border-gray-200">
      <div class="flex items-center font-semibold text-gray-700 justify-between">
        <span class="text-xs font-semiboldyghgbn  text-gray-600">{{ $t('total_selected_objects') }}</span>
        <span class="text-xs font-semibold text-gray-900 bg-gray-50 px-1.5 py-0.5 rounded flex-shrink-0 ml-1">
          {{ selectedEntities.length }}
          <span v-if="company.alias === 'x-elio' && layer.code === 'modules'">({{ totalGroupByStatusDesign }})</span>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
  import {mapGetters} from 'vuex'

  export default {
    name: 'SelectedElementsSummary',

    props: {
      statuses: {
        type: Array,
        default: []
      },

      selectedEntities: {
        type: Array,
        default: []
      },

      typeStatus: {
        type: String,
        default: 'progress'
      },

      designData: {
        type: Object,
        default: null
      },

      layer: {
        type: Object,
        default: null
      }
    },

    data() {
      return {
      }
    },

    computed: {
      ...mapGetters({
        company: 'app/company',
      }),

      groupedByStatus() {
        // Si no hay entidades, devolvemos un objeto vacío inmediatamente
        if (!this.selectedEntities || this.selectedEntities.length === 0) {
          return {};
        }

        return this.selectedEntities.reduce((acc, entity) => {
          let key = null;
          if (this.typeStatus === 'incident') {
            key = (entity.picIncident != null && typeof entity.picIncident === 'object') ? entity.picIncident.id : entity.picIncident;  // Propiedad para agrupar
          } else {
            key = (entity.picStatus != null && typeof entity.picStatus === 'object') ? entity.picStatus.id : entity.picStatus;  // Propiedad para agrupar
          }
          acc[key] = (acc[key] || 0) + 1;  // Incrementa o inicializa el contador
          return acc;
        }, {});
      },

      groupByStatusDesign() {
        // Si no hay entidades, devolvemos un objeto vacío inmediatamente
        if (!this.selectedEntities || this.selectedEntities.length === 0) {
          return {};
        }

        return this.selectedEntities.reduce((acc, entity) => {
          let key = null;
          if (this.typeStatus === 'incident') {
            key = (entity.picIncident != null && typeof entity.picIncident === 'object') ? entity.picIncident.id : entity.picIncident;  // Propiedad para agrupar
          } else {
            key = (entity.picStatus != null && typeof entity.picStatus === 'object') ? entity.picStatus.id : entity.picStatus;  // Propiedad para agrupar
          }

          // Obtener el nombre de la capa y limpiar el prefijo
          const layerEntity = (entity.picLayerName || '').replace('MODULES_EPC_', '').replace(/ /g, '_');

          // Obtener datos del diseño para esta capa
          const layerDesign = this.designData?.[layerEntity] || this.designData?.default || {};
          const totalPanels = layerDesign.total_panels || 1;
          const totalAxles = layerDesign.total_axles || 1;

          // Multiplicador: solo aplica si panels > 1
          const multiplier = totalPanels > 1 ? (totalPanels * totalAxles) : 1;

          acc[key] = (acc[key] || 0) + multiplier;  // Incrementa con el multiplicador
          return acc;
        }, {});
      },

      totalGroupByStatusDesign() {
        return Object.values(this.groupByStatusDesign).reduce((sum, count) => sum + count, 0);
      }
    }
  }
</script>
